import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CloseButton } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import { resetSelectedAirAmbulance } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  AXON_MAP_CARD,
  DYNAMIC_LAYERS_CARD,
  RADE_MAP_CARD,
  STATIC_LAYERS_CARD,
  closeAllCards,
  EAGLE_EYE_MAP_CARD,
  MEDIA_CARD,
  LIVE_VIDEO_CARD,
  OUTBOUND_SMS_CARD,
  ROADSIDE_ASSISTANCE_CARD,
  AIR_AMBULANCE_CARD,
  PURE_WIRELESS_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import usePortalType from '@rsos/utils/usePortalType';
import { trackCloseAirAmbulanceRequestForm } from '../../gainsightTrackings/airAmbulanceTrackings';
import MediaCard from '../AdditionalData/MediaCard';
import AirAmbulanceForm from '../AirAmbulanceForm';
import { OverridedAirAmbulanceStyledContainer } from '../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import ScrollShadow from '../Common/ScrollShadow/ScrollShadow';
import { OutboundSMS } from '../JurisdictionView/ActionBar/OutboundSMS/OutboundSMS';
import { OverridedStyledContainer } from '../JurisdictionView/ActionBar/OutboundSMS/styles/OutboundSMS.styles';
import { RoadsideAssistance } from '../JurisdictionView/ActionBar/RoadsideAssistance/RoadsideAssistance';
import AviveContainer, {
  hidePlaceholders,
} from '../JurisdictionView/AviveAED/AviveContainer';
import { LiveVideoCard } from '../JurisdictionView/CallChats/VideoTo911/LiveVideoCard';
import { trackTogglingOutboundSMSCard } from '../JurisdictionView/CallChats/textFrom911Trackings';
import { trackHideVideoCard } from '../JurisdictionView/CallChats/videoTo911Trackings';
import RADECardContainer from '../JurisdictionView/RADECard/RADECardContainer';
import RADECardNext from '../JurisdictionView/RADECard/RADECardNext';
import {
  clearAdrLookup,
  clearMafLookup,
  clearSelectedLookup,
  hide,
} from '../JurisdictionView/RADECard/radeSlice';
import { reset } from '../JurisdictionView/UniversalSearch/searchSlice';
import { trackCloseRADECard } from '../JurisdictionView/jurisdictionViewTrackings';
import TextTo911Container from '../TextTo911/TextTo911Container';
import DynamicLayerCard from './DynamicLayerCard';
import { StyledContainer } from './MapCard.styles';
import PureWirelessCard from './PureWirelessCard';
import StaticLayerCard from './StaticLayerCard';

/**
 * Container component with the app logic to render different map cards
 * @param {Boolean} hasEsriMapAccess - Flag for if user has access to Esri maps
 * @param {Object} mapRef - mapRef used to calculate the height of the map
 */
const MapCard = ({ hasEsriMapAccess, mapRef, setMafError, setADRError }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const { irpVersion } = useSelector(state => state.sinatra.irp);
  const selectedLocation = useSelector(
    state => state.gisSearch.selectedLocation,
  );
  const { portalType } = usePortalType();

  const { cards, shouldHideHeader } = useSelector(state => state.mapCard);
  const hasGenericResourceRequests = useFlag('generic_resource_requests');

  // Check if any cards are active
  const hasActiveCards = Object.values(cards).some(isActive => isActive);

  /**
   * Handle closing all cards
   * @param {Function} callbackFn - Callback function for custom handling when
   * closing certain cards
   */
  const handleCloseCard = useCallback(
    (callbackFn = () => {}) => {
      dispatch(closeAllCards());
      callbackFn();
    },
    [dispatch],
  );

  const handleCloseRadECard = useCallback(() => {
    dispatch(hide());
    // Also clear all GlobalMapSearch-scoped RAD-e lookup channels
    // (consumed by RADECardNext) so re-opening the card later
    // doesn't resurface a stale id / dataset from a prior search.
    // We clear both MAF and ADR (the dropdown can have either or
    // both pending) plus the `selectedLookup` pointer that decided
    // which channel RADECardNext was rendering.
    dispatch(clearMafLookup());
    dispatch(clearAdrLookup());
    dispatch(clearSelectedLookup());
    // Track when closing RADECard since it's not explicitly closing with the RADECard's close button
    if (cards[RADE_MAP_CARD]) {
      trackCloseRADECard(irpVersion, portalType);
      if (!selectedLocation) {
        dispatch(reset());
      }
    }
  }, [cards, dispatch, irpVersion, portalType, selectedLocation]);

  const trackCloseCard = useCallback(() => {
    if (cards[OUTBOUND_SMS_CARD]) {
      trackTogglingOutboundSMSCard({
        show: false,
        isMapCardCloseBtn: true,
      });
    }
    if (cards[LIVE_VIDEO_CARD]) {
      trackHideVideoCard();
    }
    if (cards[AIR_AMBULANCE_CARD]) {
      trackCloseAirAmbulanceRequestForm();
    }
  }, [cards]);

  useEffect(() => {
    // hide avive_aed icon when cards are switched or closed outside of onClose button
    if (!cards[AVIVE_APP_LAUNCHER_MAP_CARD]) {
      hidePlaceholders();
    }
  }, [cards]);

  const isEventPaneCollapsed = useSelector(
    state => state.eventPane.isCollapsed,
  );

  // If no cards are active, don't render anything
  if (!hasActiveCards) {
    return null;
  }

  return (
    <StyledContainer
      hasEsriMapAccess={hasEsriMapAccess}
      mapRef={mapRef}
      ref={containerRef}
      isEventPaneCollapsed={isEventPaneCollapsed}
    >
      {!shouldHideHeader && (
        <CloseButton
          absolute
          onClose={() => {
            trackCloseCard();
            hidePlaceholders();
            handleCloseCard();
            handleCloseRadECard();
            dispatch(resetSelectedAirAmbulance());
          }}
          right={16}
          top={16}
          zIndex={1}
        />
      )}
      <Box>
        {Object.keys(cards).map(key => {
          const shouldRender = cards[key];
          if (shouldRender) {
            if (key === AGENT_511_MAP_CARD) {
              return <TextTo911Container key={key} onClose={handleCloseCard} />;
            } else if (key === AVIVE_APP_LAUNCHER_MAP_CARD) {
              return (
                <AviveContainer
                  key={key}
                  shouldRender={shouldRender}
                  onClose={handleCloseCard}
                />
              );
            } else if (key === RADE_MAP_CARD) {
              // Two RAD-e containers are mounted side-by-side and
              // self-gate based on which slice channel is active:
              //   - RADECardContainer (legacy) renders only when
              //     `state.rade.showing` is true, set by
              //     ConnectedUniversalSearch's `showRADE` dispatch.
              //   - RADECardNext renders only when
              //     `state.rade.selectedLookup` is non-null, set by
              //     GlobalMapSearch when the user clicks a RAD-e
              //     row in the dropdown (see `useResultActions`).
              // The two channels are mutually exclusive in practice
              // because each entry point only writes its own.
              return (
                <React.Fragment key={key}>
                  <RADECardContainer
                    setMafError={setMafError}
                    setADRError={setADRError}
                  />
                  <RADECardNext />
                </React.Fragment>
              );
            } else if (key === AXON_MAP_CARD || key === EAGLE_EYE_MAP_CARD) {
              // TODO: can combine these, but due to different implementations of
              // the layers between deck.gl and google maps, it might cause
              // unexpected issues. revisit in future update
              return <DynamicLayerCard key={key} />;
            } else if (key === DYNAMIC_LAYERS_CARD) {
              return <DynamicLayerCard key={key} />;
            } else if (key === STATIC_LAYERS_CARD) {
              return <StaticLayerCard key={key} />;
            } else if (key === PURE_WIRELESS_CARD) {
              return <PureWirelessCard key={key} />;
            } else if (key === MEDIA_CARD) {
              return <MediaCard key={key} />;
            } else if (key === LIVE_VIDEO_CARD) {
              return <LiveVideoCard key={key} />;
            } else if (key === AIR_AMBULANCE_CARD) {
              if (hasGenericResourceRequests) {
                return null;
              }
              return (
                <React.Fragment key={key}>
                  <OverridedAirAmbulanceStyledContainer />
                  <AirAmbulanceForm
                    fromMapCard
                    handleCloseCard={handleCloseCard}
                    key={key}
                  />
                  <ScrollShadow containerRef={containerRef} />
                </React.Fragment>
              );
            } else if (key === OUTBOUND_SMS_CARD) {
              return (
                <React.Fragment key={key}>
                  <OverridedStyledContainer />
                  <OutboundSMS key={key} />
                </React.Fragment>
              );
            } else if (key === ROADSIDE_ASSISTANCE_CARD) {
              return (
                <React.Fragment key={key}>
                  <OverridedStyledContainer />
                  <RoadsideAssistance key={key} />
                </React.Fragment>
              );
            }
            return null;
          }
        })}
      </Box>
    </StyledContainer>
  );
};

export default React.memo(MapCard);
